import * as THREE from 'three';
import { CityGenerator, CityConfig } from './CityGenerator';
import { LandmarkPlacer } from './LandmarkPlacer';
import { JumpRamp, JumpRampConfig } from './JumpRamp';
import { Collectible, CollectibleConfig } from '../entities/Collectible';
import { PhysicsWorld } from '../physics/PhysicsWorld';
import { HighwayManager } from './HighwayManager';
import { TerrainManager, TerrainGenerator } from './terrain';

export interface WorldConfig {
  cityConfig?: Partial<CityConfig>;
  enableLandmarks?: boolean;
  enableRamps?: boolean;
  enableCollectibles?: boolean;
  enableHighways?: boolean;
  enableTerrain?: boolean;
}

export class WorldGenerator {
  private scene: THREE.Scene;
  private physicsWorld: PhysicsWorld;
  private cityGenerator: CityGenerator;
  private landmarkPlacer: LandmarkPlacer;
  private highwayManager: HighwayManager | null = null;
  private terrainManager: TerrainManager | null = null;
  private jumpRamps: JumpRamp[] = [];
  private collectibles: Collectible[] = [];
  private config: WorldConfig;

  constructor(
    scene: THREE.Scene,
    physicsWorld: PhysicsWorld,
    config?: WorldConfig
  ) {
    this.scene = scene;
    this.physicsWorld = physicsWorld;
    this.config = {
      enableLandmarks: true,
      enableRamps: true,
      enableCollectibles: true,
      enableHighways: true,
      enableTerrain: true,
      ...config,
    };

    this.cityGenerator = new CityGenerator(scene, physicsWorld, config?.cityConfig);
    this.landmarkPlacer = new LandmarkPlacer(scene, physicsWorld);
  }

  public generate(): void {
    // Generate procedural city
    this.cityGenerator.generate();

    // Place hand-crafted landmarks
    if (this.config.enableLandmarks) {
      this.landmarkPlacer.placeAll();
    }

    // Generate elevated highway network
    if (this.config.enableHighways) {
      this.highwayManager = new HighwayManager(this.scene);
      this.highwayManager.generate();
    }

    // Generate terrain system (mountains, hills, craters)
    if (this.config.enableTerrain) {
      this.terrainManager = new TerrainManager(this.scene);
      const terrainGenerator = new TerrainGenerator(this.scene, this.terrainManager);
      terrainGenerator.generate();
    }

    // Create jump ramps
    if (this.config.enableRamps) {
      this.createJumpRamps();
    }

    // Spawn collectibles
    if (this.config.enableCollectibles) {
      this.spawnCollectibles();
    }
  }

  private createJumpRamps(): void {
    const rampConfigs: JumpRampConfig[] = [
      // === SPAWN AREA RAMP - Directly ahead of spawn point ===
      {
        position: new THREE.Vector3(0, 0, 20),
        rotation: 0,
        rampLength: 12,
        rampHeight: 3,
        rampWidth: 8,
        hoopDistance: 15,
        hoopHeight: 6,
      },

      // === GROUND LEVEL MEGA-RAMPS (4) - Reach Tier 1 ===
      // North
      {
        position: new THREE.Vector3(0, 0, 100),
        rotation: 0,
        rampLength: 20,
        rampHeight: 6,
        rampWidth: 10,
        hoopDistance: 20,
        hoopHeight: 10,
      },
      // East
      {
        position: new THREE.Vector3(100, 0, 0),
        rotation: -Math.PI / 2,
        rampLength: 20,
        rampHeight: 6,
        rampWidth: 10,
        hoopDistance: 20,
        hoopHeight: 10,
      },
      // South
      {
        position: new THREE.Vector3(0, 0, -100),
        rotation: Math.PI,
        rampLength: 20,
        rampHeight: 6,
        rampWidth: 10,
        hoopDistance: 20,
        hoopHeight: 10,
      },
      // West
      {
        position: new THREE.Vector3(-100, 0, 0),
        rotation: Math.PI / 2,
        rampLength: 20,
        rampHeight: 6,
        rampWidth: 10,
        hoopDistance: 20,
        hoopHeight: 10,
      },

      // === TIER 1 SUPER-RAMPS (4) - Reach Tier 2 from highways ===
      // NW diagonal
      {
        position: new THREE.Vector3(-200, 8, 200),
        rotation: -Math.PI / 4,
        rampLength: 25,
        rampHeight: 10,
        rampWidth: 12,
        hoopDistance: 25,
        hoopHeight: 20,
      },
      // SE diagonal
      {
        position: new THREE.Vector3(200, 8, -200),
        rotation: (3 * Math.PI) / 4,
        rampLength: 25,
        rampHeight: 10,
        rampWidth: 12,
        hoopDistance: 25,
        hoopHeight: 20,
      },
      // NE diagonal
      {
        position: new THREE.Vector3(200, 8, 200),
        rotation: (-3 * Math.PI) / 4,
        rampLength: 25,
        rampHeight: 10,
        rampWidth: 12,
        hoopDistance: 25,
        hoopHeight: 20,
      },
      // SW diagonal
      {
        position: new THREE.Vector3(-200, 8, -200),
        rotation: Math.PI / 4,
        rampLength: 25,
        rampHeight: 10,
        rampWidth: 12,
        hoopDistance: 25,
        hoopHeight: 20,
      },

      // === TIER 3 SKY RAMPS (3) - Reach Tier 4 sky loops from beltway (radius 580) ===
      // West beltway
      {
        position: new THREE.Vector3(-580, 30, 0),
        rotation: Math.PI / 2,
        rampLength: 30,
        rampHeight: 15,
        rampWidth: 14,
        hoopDistance: 30,
        hoopHeight: 45,
      },
      // East beltway
      {
        position: new THREE.Vector3(580, 30, 0),
        rotation: -Math.PI / 2,
        rampLength: 30,
        rampHeight: 15,
        rampWidth: 14,
        hoopDistance: 30,
        hoopHeight: 45,
      },
      // NW corner of beltway (580 * 0.707 ≈ 410)
      {
        position: new THREE.Vector3(-410, 30, 410),
        rotation: -Math.PI / 4,
        rampLength: 28,
        rampHeight: 14,
        rampWidth: 12,
        hoopDistance: 28,
        hoopHeight: 42,
      },
    ];

    for (const config of rampConfigs) {
      const ramp = new JumpRamp(this.scene, this.physicsWorld, config);
      this.jumpRamps.push(ramp);
    }
  }

  private spawnCollectibles(): void {
    const collectibleConfigs: CollectibleConfig[] = [
      // === GROUND LEVEL (Tier 0) - Basic pickups ===
      // Health pickups scattered around
      { position: new THREE.Vector3(30, 0, 30), type: 'health', value: 25, respawnTime: 30 },
      { position: new THREE.Vector3(-30, 0, 30), type: 'health', value: 25, respawnTime: 30 },
      { position: new THREE.Vector3(30, 0, -30), type: 'health', value: 25, respawnTime: 30 },
      { position: new THREE.Vector3(-30, 0, -30), type: 'health', value: 25, respawnTime: 30 },
      { position: new THREE.Vector3(80, 0, 0), type: 'health', value: 50, respawnTime: 45 },
      { position: new THREE.Vector3(-80, 0, 0), type: 'health', value: 50, respawnTime: 45 },

      // Ammo pickups
      { position: new THREE.Vector3(50, 0, 50), type: 'ammo', value: 50, respawnTime: 20 },
      { position: new THREE.Vector3(-50, 0, 50), type: 'ammo', value: 50, respawnTime: 20 },
      { position: new THREE.Vector3(50, 0, -50), type: 'ammo', value: 50, respawnTime: 20 },
      { position: new THREE.Vector3(-50, 0, -50), type: 'ammo', value: 50, respawnTime: 20 },
      { position: new THREE.Vector3(0, 0, 80), type: 'ammo', value: 100, respawnTime: 25 },
      { position: new THREE.Vector3(0, 0, -80), type: 'ammo', value: 100, respawnTime: 25 },

      // Intel pickups (mission objectives, no respawn)
      { position: new THREE.Vector3(100, 0, 100), type: 'intel', value: 1000, respawnTime: 0 },
      { position: new THREE.Vector3(-100, 0, -100), type: 'intel', value: 1000, respawnTime: 0 },
      { position: new THREE.Vector3(100, 0, -100), type: 'intel', value: 1000, respawnTime: 0 },
      { position: new THREE.Vector3(-100, 0, 100), type: 'intel', value: 1000, respawnTime: 0 },

      // Shield pickups (rare)
      { position: new THREE.Vector3(0, 0, 0), type: 'shield', value: 50, respawnTime: 60 },
      { position: new THREE.Vector3(70, 0, 70), type: 'shield', value: 25, respawnTime: 45 },
      { position: new THREE.Vector3(-70, 0, -70), type: 'shield', value: 25, respawnTime: 45 },

      // === TIER 1 (Height 8) - Temporary Buffs ===
      // On the N-S highways
      { position: new THREE.Vector3(-300, 8, 0), type: 'buff_damage', value: 0, respawnTime: 45 },
      { position: new THREE.Vector3(300, 8, 0), type: 'buff_firerate', value: 0, respawnTime: 45 },
      // On the E-W highways
      { position: new THREE.Vector3(0, 8, -300), type: 'buff_speed', value: 0, respawnTime: 45 },
      { position: new THREE.Vector3(0, 8, 300), type: 'buff_damage', value: 0, respawnTime: 45 },
      // At intersections
      { position: new THREE.Vector3(-100, 8, 100), type: 'buff_firerate', value: 0, respawnTime: 60 },
      { position: new THREE.Vector3(100, 8, -100), type: 'buff_speed', value: 0, respawnTime: 60 },

      // === TIER 2 (Height 18) - Better Buffs ===
      // On the diagonal highways
      { position: new THREE.Vector3(-200, 18, 200), type: 'buff_damage', value: 0, respawnTime: 60 },
      { position: new THREE.Vector3(200, 18, -200), type: 'buff_firerate', value: 0, respawnTime: 60 },
      { position: new THREE.Vector3(200, 18, 200), type: 'buff_speed', value: 0, respawnTime: 60 },
      { position: new THREE.Vector3(-200, 18, -200), type: 'buff_damage', value: 0, respawnTime: 60 },
      // Center crossing
      { position: new THREE.Vector3(0, 18, 0), type: 'buff_firerate', value: 0, respawnTime: 45 },

      // === TIER 3 (Height 30) - New Weapons ===
      // On the octagonal beltway (radius 580) - weapon unlocks (no respawn)
      { position: new THREE.Vector3(580, 30, 0), type: 'weapon_railgun', value: 0, respawnTime: 0 },
      { position: new THREE.Vector3(-580, 30, 0), type: 'weapon_plasma', value: 0, respawnTime: 0 },
      { position: new THREE.Vector3(0, 30, 580), type: 'weapon_missiles', value: 0, respawnTime: 0 },
      { position: new THREE.Vector3(0, 30, -580), type: 'weapon_railgun', value: 0, respawnTime: 0 },
      // Diagonal positions on beltway (580 * 0.707 ≈ 410)
      { position: new THREE.Vector3(410, 30, 410), type: 'weapon_plasma', value: 0, respawnTime: 0 },
      { position: new THREE.Vector3(-410, 30, -410), type: 'weapon_missiles', value: 0, respawnTime: 0 },

      // === TIER 4 (Height 45) - Permanent Upgrades (Jump-only access) ===
      // Northwest sky loop
      { position: new THREE.Vector3(-350, 45, 350), type: 'perm_firerate', value: 0, respawnTime: 0 },
      { position: new THREE.Vector3(-310, 45, 350), type: 'perm_magazine', value: 0, respawnTime: 0 },
      { position: new THREE.Vector3(-390, 45, 350), type: 'perm_projspeed', value: 0, respawnTime: 0 },
      // Southeast sky loop
      { position: new THREE.Vector3(350, 45, -350), type: 'perm_firerate', value: 0, respawnTime: 0 },
      { position: new THREE.Vector3(310, 45, -350), type: 'perm_magazine', value: 0, respawnTime: 0 },
      { position: new THREE.Vector3(390, 45, -350), type: 'perm_projspeed', value: 0, respawnTime: 0 },
    ];

    for (const config of collectibleConfigs) {
      const collectible = new Collectible(this.scene, config);
      this.collectibles.push(collectible);
    }
  }

  public update(dt: number, playerPosition: THREE.Vector3): void {
    // Check jump ramp hoops
    for (const ramp of this.jumpRamps) {
      ramp.checkHoopPass(playerPosition);
    }

    // Update collectibles
    for (const collectible of this.collectibles) {
      collectible.update(dt, playerPosition);
    }
  }

  // Get the ground height at a position (accounts for terrain, ramps, and highways)
  public getGroundHeight(position: THREE.Vector3): number {
    let maxHeight = 0;

    // Check terrain
    if (this.terrainManager) {
      const terrainResult = this.terrainManager.getHeightAtPosition(position);
      if (terrainResult.height !== null && terrainResult.height > maxHeight) {
        maxHeight = terrainResult.height;
      }
    }

    // Check all ramps
    for (const ramp of this.jumpRamps) {
      const rampHeight = ramp.getHeightAtPosition(position);
      if (rampHeight !== null && rampHeight > maxHeight) {
        maxHeight = rampHeight;
      }
    }

    // Check highways
    if (this.highwayManager) {
      const highwayResult = this.highwayManager.getHeightAtPosition(position);
      if (highwayResult.onHighway && highwayResult.height > maxHeight) {
        maxHeight = highwayResult.height;
      }
    }

    return maxHeight;
  }

  // Multi-point height sampling for better ramp/highway/terrain detection
  public getGroundHeightMultiPoint(
    position: THREE.Vector3,
    forward: THREE.Vector3,
    wheelBase: number = 2.0,
    trackWidth: number = 1.0
  ): { height: number; onRamp: boolean; onHighway: boolean; onTerrain: boolean; currentTier: number; frontHeight: number; backHeight: number } {
    // Calculate right vector
    const up = new THREE.Vector3(0, 1, 0);
    const right = new THREE.Vector3().crossVectors(up, forward).normalize();

    // Sample 4 wheel positions
    const halfWheelBase = wheelBase / 2;
    const halfTrack = trackWidth / 2;

    const frontLeft = position.clone()
      .add(forward.clone().multiplyScalar(halfWheelBase))
      .add(right.clone().multiplyScalar(-halfTrack));
    const frontRight = position.clone()
      .add(forward.clone().multiplyScalar(halfWheelBase))
      .add(right.clone().multiplyScalar(halfTrack));
    const backLeft = position.clone()
      .add(forward.clone().multiplyScalar(-halfWheelBase))
      .add(right.clone().multiplyScalar(-halfTrack));
    const backRight = position.clone()
      .add(forward.clone().multiplyScalar(-halfWheelBase))
      .add(right.clone().multiplyScalar(halfTrack));

    // Get heights at each point
    const heights = [
      this.getGroundHeight(frontLeft),
      this.getGroundHeight(frontRight),
      this.getGroundHeight(backLeft),
      this.getGroundHeight(backRight),
    ];

    // Calculate front and back average heights (for detecting slope direction)
    const frontHeight = (heights[0] + heights[1]) / 2;
    const backHeight = (heights[2] + heights[3]) / 2;

    // Use maximum height to prevent clipping through ramps
    const maxHeight = Math.max(...heights);
    const onRamp = maxHeight > 0.1;

    // Check if on highway and get tier
    let onHighway = false;
    let currentTier = 0;
    if (this.highwayManager) {
      const highwayResult = this.highwayManager.getHeightAtPosition(position);
      onHighway = highwayResult.onHighway;
      currentTier = highwayResult.currentTier;
    }

    // Check if on terrain
    let onTerrain = false;
    if (this.terrainManager) {
      const terrainResult = this.terrainManager.getHeightAtPosition(position);
      onTerrain = terrainResult.height !== null && terrainResult.height > 0.5;
    }

    return {
      height: maxHeight,
      onRamp,
      onHighway,
      onTerrain,
      currentTier,
      frontHeight,
      backHeight,
    };
  }

  public getJumpRamps(): JumpRamp[] {
    return this.jumpRamps;
  }

  public getCollectibles(): Collectible[] {
    return this.collectibles;
  }

  public getActiveCollectibles(): Collectible[] {
    return this.collectibles.filter((c) => c.isActive());
  }

  public getCityGenerator(): CityGenerator {
    return this.cityGenerator;
  }

  public getLandmarkPlacer(): LandmarkPlacer {
    return this.landmarkPlacer;
  }

  public getHighwayManager(): HighwayManager | null {
    return this.highwayManager;
  }

  public getTerrainManager(): TerrainManager | null {
    return this.terrainManager;
  }

  public destroy(): void {
    this.cityGenerator.destroy();
    this.landmarkPlacer.destroy();

    if (this.highwayManager) {
      this.highwayManager.destroy();
      this.highwayManager = null;
    }

    if (this.terrainManager) {
      this.terrainManager.destroy();
      this.terrainManager = null;
    }

    for (const ramp of this.jumpRamps) {
      ramp.destroy();
    }

    for (const collectible of this.collectibles) {
      collectible.destroy();
    }

    this.jumpRamps = [];
    this.collectibles = [];
  }
}
